import { Home, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { folderLogger } from '../lib/logger'
import { ALBUM_DRAG_MIME, parseAlbumDrag } from '../lib/album-drag-payload'
import type { Album } from "../../../../types"

interface BreadcrumbsProps {
  breadcrumbPath: Array<{ id: string; name: string }>
  /** What the root crumb is called: the Memoir's name inside one (SOL-204). */
  rootLabel?: string
  onNavigate: (folderId?: string) => void
  onAlbumDrop?: (album: Album | null, targetAlbumId: string | undefined) => void
  draggedAlbum?: Album | null
  isDragging?: boolean
  className?: string
}

export default function Breadcrumbs({ 
  breadcrumbPath, 
  rootLabel = 'Home',
  onNavigate,
  onAlbumDrop,
  draggedAlbum,
  isDragging = false,
  className = '' 
}: BreadcrumbsProps): JSX.Element {
  const [dragOverItem, setDragOverItem] = useState<string | null>(null)
  
  folderLogger.debug('Breadcrumbs rendering', {
    breadcrumbPathLength: breadcrumbPath.length,
    breadcrumbPath,
    isDragging
  })

  const handleDragOver = (e: React.DragEvent, targetId?: string) => {
    if (!isDragging && !e.dataTransfer.types.includes(ALBUM_DRAG_MIME)) return
    
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    
    const dragOverKey = targetId || 'root'
    if (dragOverItem !== dragOverKey) {
      setDragOverItem(dragOverKey)
      folderLogger.debug('Drag over breadcrumb', { targetId })
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear drag over if we're actually leaving the breadcrumb area
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverItem(null)
    }
  }

  const handleDrop = (e: React.DragEvent, targetId?: string) => {
    e.preventDefault()
    setDragOverItem(null)
    
    try {
      folderLogger.debug('Drop on breadcrumb', {
        targetId: targetId || 'root',
        hasDataTransfer: e.dataTransfer.files.length === 0
      })
      
      // Handle album drops from dataTransfer. The shape is owned by
      // lib/album-drag-payload (SOL-192); AlbumCard writes it with the
      // matching serializer.
      const droppedAlbum = parseAlbumDrag(e.dataTransfer.getData(ALBUM_DRAG_MIME))

      if (droppedAlbum) {
        folderLogger.info('Album dropped on breadcrumb via dataTransfer', {
          droppedAlbumId: droppedAlbum.uuid,
          targetId: targetId || 'root'
        })
        
        onAlbumDrop?.(droppedAlbum, targetId)
      } else if (isDragging && draggedAlbum) {
        folderLogger.info('Album dropped on breadcrumb via internal state', { 
          droppedAlbumId: draggedAlbum.uuid,
          targetId: targetId || 'root'
        })
        
        onAlbumDrop?.(null, targetId)
      }
    } catch (error) {
      folderLogger.error('Error handling drop on breadcrumb', { error })
    }
  }

  return (
    <nav className={`breadcrumbs ${className}`} aria-label="Breadcrumb">
      <div className="breadcrumb-segment">
        <button
          className={`breadcrumb-item breadcrumb-item--root ${
            dragOverItem === 'root' ? 'breadcrumb-item--drag-over' : ''
          }`}
          onClick={() => onNavigate(undefined)}
          onDragOver={(e) => handleDragOver(e, undefined)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, undefined)}
          title={rootLabel}
        >
          <Home size={16} />
          <span>{rootLabel}</span>
        </button>
      </div>
      
      {breadcrumbPath.map((crumb) => (
        <div key={crumb.id} className="breadcrumb-segment">
          <div className="breadcrumb-separator">
            <ChevronRight size={14} />
          </div>
          <button
            className={`breadcrumb-item ${
              dragOverItem === crumb.id ? 'breadcrumb-item--drag-over' : ''
            }`}
            onClick={() => onNavigate(crumb.id)}
            onDragOver={(e) => handleDragOver(e, crumb.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, crumb.id)}
            title={crumb.name}
          >
            <span>{crumb.name}</span>
          </button>
        </div>
      ))}
    </nav>
  )
} 